import { useCallback, useState, useMemo, useEffect } from 'react';
import {
    setCurrentTableView,
    createAndSaveTableView,
    deleteAndSaveTableViews,
    tblSpecLoadStorage,
} from '../tableViewsRepo';
import { TableViewFormData, LegacyTableView, TblViewSpec } from '../table-view.types';
import { splitDefaultAndSessionViews } from '../utils/tableViewHelpers';
import { feedsListAllColumnsView } from '../../../ops/feeds/list/table/FeedsListTableSpec';
import { SavedViewsFlyOutMode } from '../TableViewEditForm';

const generateDefaultView = (defaultSpec: TblViewSpec): LegacyTableView => ({
    id: '1',
    title: 'Default',
    sortby: defaultSpec.sortby || '',
    pgsize: 50,
    cols:
        defaultSpec.sections
            .flatMap(section => section.cols)
            .map(col => `|${col}`)
            .join('') + '|',
});

export type TableViewPersistenceActions = {
    flyOutMode: SavedViewsFlyOutMode;
    editingView: LegacyTableView | null;
    handleCreateView: () => void;
    handleEditView: (view: LegacyTableView) => void;
    handleCloseFlyOut: () => void;
    handleFlyOutSave: (viewData: TableViewFormData) => Promise<void>;
    handleViewDelete: (viewId: string) => Promise<void>;
    tableViews: LegacyTableView[];
};

export type UseTableViewPersistenceParams = {
    tableViews: LegacyTableView[];
    tableId: string;
    setCurrentViewId: (id: string) => void;
    setTableState: (fn: (draft: any) => void) => void;
    flyOutMode: SavedViewsFlyOutMode;
    setFlyOutMode: (mode: SavedViewsFlyOutMode) => void;
    setShowFlyOut: (show: boolean) => void;
    colMap: Map<string, any>; // Pass the canonical colMap from feature
    defaultView: TblViewSpec; // Pass the canonical defaultView from feature
};

export function useTableViewPersistence({
    tableViews: rawTableViews,
    tableId,
    setCurrentViewId,
    setTableState,
    flyOutMode,
    setFlyOutMode,
    setShowFlyOut,
    colMap,
    defaultView,
}: UseTableViewPersistenceParams): TableViewPersistenceActions {
    // Always merge in the Default view if missing
    const tableViews = useMemo(() => {
        const hasDefault = rawTableViews.some(v => v.id === '1');
        if (hasDefault) return rawTableViews;
        // Synthesize a Default view from feedsListAllColumnsView
        const defaultSpec = feedsListAllColumnsView();
        const legacyDefaultView = generateDefaultView(defaultSpec);
        return [legacyDefaultView, ...rawTableViews];
    }, [rawTableViews]);
    const [editingView, setEditingView] = useState<LegacyTableView | null>(null);
    const [pendingViewId, setPendingViewId] = useState<string | null>(null);

    const handleCreateView = useCallback(() => {
        setFlyOutMode('CREATE');
        setEditingView(null);
        setShowFlyOut(true);
    }, [setFlyOutMode, setShowFlyOut]);

    const handleEditView = useCallback(
        (view: LegacyTableView) => {
            if (view.id === '1' || view.id === 'default') {
                console.warn('🛡️ Blocking attempt to edit default view - default views should not be editable');
                return;
            }
            setFlyOutMode('EDIT');
            setEditingView(view);
            setShowFlyOut(true);
        },
        [setFlyOutMode, setShowFlyOut],
    );

    const handleCloseFlyOut = useCallback(() => {
        setShowFlyOut(false);
        setEditingView(null);
    }, [setShowFlyOut]);

    const handleFlyOutSave = useCallback(
        async (viewData: TableViewFormData) => {
            try {
                // Encode sort direction in sortby string for legacy format
                let sortby = viewData.sortby;
                if (viewData.sortdir === 'desc' && sortby && !sortby.startsWith('-')) {
                    sortby = '-' + sortby;
                } else if (viewData.sortdir === 'asc' && sortby && sortby.startsWith('-')) {
                    sortby = sortby.substring(1);
                }
                // Always use the existing id for EDIT mode
                const id = flyOutMode === 'EDIT' && viewData.id ? viewData.id : viewData.id || `view_${Date.now()}`;
                const savedView = {
                    id,
                    title: viewData.title,
                    sortby,
                    pgsize: viewData.pgsize,
                    cols: '|' + viewData.selectedColumns.join('|') + '|',
                };
                const storage = await createAndSaveTableView({
                    savedView,
                    flyOutMode,
                    feedsListTableId: tableId,
                });
                setTableState(draft => {
                    if (storage && Array.isArray(storage.views)) {
                        const { defaultView, sessionViews, allSpecs } = splitDefaultAndSessionViews(storage.views);
                        // Always ensure Default view is present
                        const canonicalDefault = generateDefaultView(feedsListAllColumnsView());
                        const defaultArr = Array.isArray(defaultView) ? defaultView : [defaultView];
                        const arrToCheck = defaultArr[0] ? defaultArr : allSpecs;
                        const hasDefault =
                            Array.isArray(arrToCheck) &&
                            arrToCheck.some(v => typeof v === 'object' && (v.id === '1' || v.key === '1'));
                        if (!hasDefault) {
                            draft.views = [canonicalDefault, ...(defaultView ? sessionViews : allSpecs)];
                        } else {
                            draft.views = defaultView ? [defaultView, ...sessionViews] : allSpecs;
                        }
                        if (flyOutMode === 'CREATE') {
                            draft.currentViewId = savedView.id;
                        }
                    }
                });
                // Instead of calling setCurrentViewId here, set pendingViewId
                setPendingViewId(savedView.id);
                setShowFlyOut(false);
                setEditingView(null);

                await setCurrentTableView(tableId, savedView.id);
            } catch (error) {
                console.error(`❌ Error ${flyOutMode === 'CREATE' ? 'creating' : 'updating'} view:`, error);
                throw error;
            }
        },
        [flyOutMode, tableId, setTableState, setShowFlyOut],
    );

    useEffect(() => {
        if (pendingViewId) {
            // Force reload of views from session storage before switching
            const storageKey = tableId;
            const tblViewStorage = tblSpecLoadStorage(storageKey, colMap, defaultView);
            const found = tblViewStorage.views.find(v => v.key === pendingViewId || (v as any).id === pendingViewId);
            if (found) {
                setCurrentViewId(pendingViewId);
                setPendingViewId(null);
            } else {
                // eslint-disable-next-line no-console
                console.warn(
                    '[useTableViewPersistence] Could not find pendingViewId in reloaded views:',
                    pendingViewId,
                );
            }
        }
    }, [pendingViewId, tableId, colMap, defaultView, setCurrentViewId]);

    const handleViewDelete = useCallback(
        async (viewId: string) => {
            try {
                await deleteAndSaveTableViews({
                    viewId,
                    feedsListTableId: tableId,
                });
                const filteredViews = tableViews.filter(v => v && v.id !== viewId);
                setTableState(draft => {
                    const { defaultView, sessionViews, allSpecs } = splitDefaultAndSessionViews(filteredViews);
                    // Always ensure Default view is present
                    const canonicalDefault = generateDefaultView(feedsListAllColumnsView());
                    const defaultArr = Array.isArray(defaultView) ? defaultView : [defaultView];
                    const arrToCheck = defaultArr[0] ? defaultArr : allSpecs;
                    const hasDefault =
                        Array.isArray(arrToCheck) &&
                        arrToCheck.some(v => typeof v === 'object' && (v.id === '1' || v.key === '1'));
                    if (!hasDefault) {
                        draft.views = [canonicalDefault, ...(defaultView ? sessionViews : allSpecs)];
                    } else {
                        draft.views = defaultView ? [defaultView, ...sessionViews] : allSpecs;
                    }
                    draft.currentViewId = '1';
                });
                // Ensure table spec and sort are updated for Default view
                setCurrentViewId('1');
                handleCloseFlyOut();
            } catch (error) {
                console.error('❌ Error deleting view:', error);
                throw error;
            }
        },
        [tableViews, tableId, setTableState, setCurrentViewId, handleCloseFlyOut],
    );

    return {
        flyOutMode,
        editingView,
        handleCreateView,
        handleEditView,
        handleCloseFlyOut,
        handleFlyOutSave,
        handleViewDelete,
        tableViews, // Expose canonical list with Default always present
    };
}
